<script setup lang="ts">
// Charteris Phase 2.5 Task 5 (2026-10-07): the YFS Charteris Gold & Precious
// Metals Signal tab. CHARTERIS strikes a BUY/SELL decision at 08:40 London off
// the COMEX silver (SI) move since the NY close (last bar ≤ 16:00 New York on
// the prior NY session). Same structure as JapanSignalTab.vue; both use the
// shared useLondonDecisionClock (08:40 London clock, weekend/UK-holiday
// banner, missed-publish states). Differences: SI FuturesMoveCard with no anchor note, a TSX-holiday
// banner (tsx_hold — yesterday's decision carried, a normal state, never
// degraded styling nor HOLD), no live synth NAV tile. `ctx` is accepted
// (uniform signalTab contract) and ignored — this tab polls its own data.
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { api, type CharterisSignal } from '../../api/client'
import type { SignalTabCtx } from '../nav/fundPages'
import FuturesMoveCard from './FuturesMoveCard.vue'
import { describeCharterisReason } from '../charteris/charterisReasons'
import { useLondonDecisionClock } from '../../composables/useLondonDecisionClock'
import SignalVerdict from '../signal/SignalVerdict.vue'
import NavSourceCard from '../signal/NavSourceCard.vue'
import { useCharterisLiveSynthNav } from '../../composables/useLiveSynthNav'
import LiveKtPanel from '../signal/LiveKtPanel.vue'

defineProps<{ ctx: SignalTabCtx }>()

const SIGNAL_POLL_MS = 60_000
const NAV_POLL_MS = 10 * 60_000     // the NAV lands once a day — no need to hammer it

const signal = ref<CharterisSignal | null>(null)
const navLatest = ref<{ date: string; navPence: number } | null>(null)

// London 08:40 decision clock, UK holiday calendar and the derived
// awaiting/missed/no-trade states — shared with JapanSignalTab.
const {
  todayLondon, pastDecisionTime, hasSignal, publishedToday, lateState, awaiting, noTrade,
  lastDecisionLabel, decidedAtLabel, countdown, businessDaysBetween,
} = useLondonDecisionClock(signal)

// TSX holiday on the anchor session (Review Focus 2): today's decision is
// yesterday's, carried. Shown only for today's publish — a previous day's
// carry must not decorate today's awaiting/missed states.
const tsxHold = computed(() => hasSignal.value && publishedToday.value && signal.value!.tsx_hold === true)

// The payload the subtitle and move card describe. A TSX hold is rendered as
// the TSX carry whatever `reason`/`degraded` say, so the banner, subtitle and
// card can never disagree, and the card gets no alert styling.
const displaySignal = computed<CharterisSignal | null>(() => {
  if (!hasSignal.value) return null
  return tsxHold.value ? { ...signal.value!, reason: 'tsx_holiday', degraded: false } : signal.value
})

const decisionSubtitle = computed(() => {
  // Review Focus 3: no payload yet (available:false, or the fetch failed) is a calm state.
  if (!hasSignal.value) return 'No Charteris signal yet — first decision at 08:40 London'
  if (lateState.value === 'missed') return 'No Charteris decision published today — check the signal service'
  if (lateState.value === 'unconfirmed') return 'No decision today (holiday or missed publish)'
  if (awaiting.value) {
    return pastDecisionTime.value
      ? '08:40 decision running late — not yet published'
      : 'Awaiting 08:40 decision'
  }
  return describeCharterisReason(displaySignal.value!).text
})

const statePill = computed(() => {
  if (lateState.value === 'missed') return { state: 'degraded' as const, label: 'Missed publish' }
  if (lateState.value === 'unconfirmed') return { state: 'degraded' as const, label: 'Unconfirmed' }
  if (displaySignal.value?.degraded) return { state: 'degraded' as const, label: 'Degraded' }
  return null
})

// The needle only when today's decision came from the SI move; a TSX carry
// was not decided by it.
const rawSignal = computed(() => {
  const s = signal.value
  if (!hasSignal.value || lateState.value || awaiting.value || tsxHold.value) return null
  if (s!.m_0840 == null || !s!.threshold_bps) return null
  return (s!.m_0840 * 10000) / s!.threshold_bps
})

const verdictProps = computed(() => {
  const s = hasSignal.value && !lateState.value && !awaiting.value ? signal.value! : null
  return {
    fund: 'CHARTERIS' as const,
    decision: (s?.decision?.toLowerCase() ?? null) as 'buy' | 'sell' | null,
    decisionLabel: s?.decision ?? '—',
    decisionSubtitle: decisionSubtitle.value,
    statePill: statePill.value,
    rawSignal: rawSignal.value,
    needleScale: 2,
    showNavBlock: false,
    cutoffLabel: 'Decision',
    provisional: awaiting.value,
    synthNavPence: null,
    overnightReturnPct: null,
    overnightUsdPct: null,
    anchorPence: null,
    anchorDateLabel: null,
    xdBanner: null,
    countdown: countdown.value,
    noTrade: noTrade.value,
  }
})

const staleDays = computed(() =>
  navLatest.value ? businessDaysBetween(navLatest.value.date, todayLondon.value) : null)

// --- polling -----------------------------------------------------------------
// No Dashboard feed drives Charteris (fundPages.ts has feeds: []); this tab
// self-polls like JapanSignalTab (uniform ctx contract, ctx ignored).
async function pollSignal() {
  try {
    signal.value = await api.charterisSignal()
  } catch {
    signal.value = null
  }
}
async function pollNav() {
  try {
    const r = await api.charterisNavLatest()
    navLatest.value = r && typeof r.date === 'string' && Number.isFinite(r.navPence) ? r : null
  } catch {
    navLatest.value = null
  }
}

let signalTimer: ReturnType<typeof setInterval> | null = null
let navTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  pollSignal()
  pollNav()
  signalTimer = setInterval(pollSignal, SIGNAL_POLL_MS)
  navTimer = setInterval(pollNav, NAV_POLL_MS)
})
onUnmounted(() => {
  if (signalTimer) clearInterval(signalTimer)
  if (navTimer) clearInterval(navTimer)
})

// Charteris K_t Phase 4: a compact live synthetic-NAV card beneath the verdict.
const { data: liveKt } = useCharterisLiveSynthNav()
// The card, coverage bar and holdings table are the shared LiveKtPanel (2026-10-15, also used by AmatiSignalTab).
const KT_TIER_TITLE: Record<string, string> = {
  live: 'Silver and gold futures bars under 3 minutes old',
  degraded: 'Metal futures bars 3–10 minutes old (or the market just reopened)',
  critical: 'Metal futures feeds stale while the market trades — estimate zeroed',
  closed: 'COMEX metals closed — last-close estimate',
}
</script>

<template>
  <div class="card charteris-verdict">
    <div v-if="tsxHold" class="tsx-hold-banner" data-testid="tsx-hold-banner">
      TSX holiday — yesterday's decision carried ({{ signal!.decision }}); no new deal today
    </div>
    <SignalVerdict v-bind="verdictProps" />
    <div v-if="lastDecisionLabel" class="charteris-last-decision" data-testid="charteris-last-decision">{{ lastDecisionLabel }}</div>
    <div v-if="decidedAtLabel" class="charteris-last-decision" data-testid="charteris-decided-at">{{ decidedAtLabel }}</div>
  </div>
  <FuturesMoveCard
    v-if="displaySignal"
    :signal="displaySignal"
    :today-london="todayLondon"
    symbol="SI"
    anchor-label="NY close"
    test-id="si-move-card"
    :describe="describeCharterisReason"
  />
  <NavSourceCard
    source="published"
    :kt-return-pct="null" :basket-return-pct="null" :anchor-pence="null" :anchor-date-label="null"
    :published-date="navLatest?.date ?? null"
    :published-nav-pence="navLatest?.navPence ?? null"
    :stale-days="staleDays" />
  <!-- Live K_t card + live/delayed K_t portfolio (moved from the bespoke K_t tab, 2026-10-09): price-coverage bar +
       per-holding breakdown, sorted by contribution. No chart — the page already has a chart section. -->
  <LiveKtPanel :live="liveKt" test-id-prefix="charteris" :tier-titles="KT_TIER_TITLE" tag-label="m" />
</template>

<style scoped>
.charteris-last-decision { font-size: 0.78rem; color: #8b949e; margin-top: -6px; }
.tsx-hold-banner {
  font-size: 0.82rem;
  color: #c9d1d9;
  background: #161b22;
  border-left: 3px solid #58a6ff;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 8px;
}

</style>
